/**
 * Wrap the vanilla Vani app as an island. Ring, STT, TTS stay in vani-app.js.
 * Scripts load as classic scripts so they share one global scope.
 */

import { publishPublicEnv } from '@/config/env';

const loaded = new Set();
let mountSeq = 0;

function loadScript(src) {
  if (loaded.has(src)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-vani-src="${src}"]`);
    if (existing) {
      if (existing.dataset.vaniReady === '1' || loaded.has(src)) {
        loaded.add(src);
        resolve();
        return;
      }
      existing.addEventListener('load', () => {
        loaded.add(src);
        resolve();
      }, { once: true });
      existing.addEventListener('error', () => reject(new Error(`failed to load ${src}`)), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = false;
    s.dataset.vaniSrc = src;
    s.onload = () => {
      s.dataset.vaniReady = '1';
      loaded.add(src);
      resolve();
    };
    s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });
}

export function warmupVaniIsland() {
  if (typeof document === 'undefined') return;
  [
    ['/vani/vani-knowledge.js', 'script'],
    ['/vani/vani-app.js', 'script'],
    ['/vani/vani-app.css', 'fetch'],
    ['/vani/vani-shell.html', 'fetch'],
  ].forEach(([href, as]) => {
    if (document.querySelector(`link[rel="preload"][href="${href}"]`)) return;
    const l = document.createElement('link');
    l.rel = 'preload';
    l.href = href;
    l.as = as;
    if (as === 'fetch') l.crossOrigin = 'anonymous';
    document.head.appendChild(l);
  });
  loadScript('/vani/vani-knowledge.js');
}

export async function mountVaniApp(host, { lang, session } = {}) {
  if (typeof window === 'undefined' || !host) return;
  publishPublicEnv();
  const my = ++mountSeq;
  const scriptsReady = loadScript('/vani/vani-knowledge.js').then(() => loadScript('/vani/vani-app.js'));
  const [css, html] = await Promise.all([
    fetch('/vani/vani-app.css').then((r) => {
      if (!r.ok) throw new Error('vani-app.css missing, run npm run vani:sync');
      return r.text();
    }),
    fetch('/vani/vani-shell.html').then((r) => {
      if (!r.ok) throw new Error('vani-shell.html missing, run npm run vani:sync');
      return r.text();
    }),
  ]);
  if (my !== mountSeq) return;

  const shadow = host.shadowRoot || host.attachShadow({ mode: 'open' });
  shadow.innerHTML = `<style>${css}</style>${html}`;
  host.dataset.ready = 'true';
  if (lang) host.setAttribute('lang', lang);

  /* Do not load checkout.js here. It preloads unused chunks, fires
     Invalid URI media errors, and with our old CSS overlay stole clicks
     on the waitlist page. buyPass() loads it only when paying. */
  await scriptsReady;
  if (my !== mountSeq) return;

  if (typeof window.mountVaniApp !== 'function') {
    throw new Error('mountVaniApp was not exported by vani-app.js');
  }
  await window.mountVaniApp(host, { root: shadow, lang, session });
}

export async function unmountVaniApp(host) {
  mountSeq += 1;
  if (typeof window !== 'undefined' && typeof window.unmountVaniApp === 'function') {
    await window.unmountVaniApp();
  }
  if (host?.shadowRoot) host.shadowRoot.innerHTML = '';
  if (host) delete host.dataset.ready;
}
